import { zodResolver } from "@hookform/resolvers/zod";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Loading, Panel, Typography } from "@/components/atoms";
import { GeneralLogo } from "@/components/organisms/General/GeneralLogo";
import { SubscriptionGroup } from "@/components/organisms/PlanSelection";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useUpdateProfileMutation } from "@/redux/apis/profile/profileApi";
import { getDisabledPlanInfo } from "@/redux/apis/subscription/subscriptionLimits";
import {
  useCreateSubscriptionMutation,
  useGetSubscriptionPlansQuery,
  useLazyGetStripeCustomerQuery,
  useLazyGetPaymentMethodsQuery,
} from "@/redux/apis/subscription/subscriptionPlansApi";
import { useAppSelector } from "@/redux/hooks";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { addToast } from "@/utils/addToast";
import { formatCurrency } from "@/utils/formatCurrency";
import { AddCardForm } from "./AddCardForm";
import { PaymentMethodCard } from "./PaymentMethodCard";

const formSchema = z.object({
  subscription: z.string().min(1, "You must select a subscription plan to continue"),
  paymentMethod: z.string().min(1, "You must select or add a payment method to continue"),
});

type FormData = z.infer<typeof formSchema>;

interface PlanSelectionStepProps {
  onPlanSelected: () => void;
}

export const PlanSelectionStep = ({ onPlanSelected }: PlanSelectionStepProps) => {
  const operator = useAppSelector(activeOperatorSelector);
  const [updateProfile] = useUpdateProfileMutation();
  const [createSubscription] = useCreateSubscriptionMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [addCardKey, setAddCardKey] = useState(0);

  // Stripe-related state
  const [stripeCustomerId, setStripeCustomerId] = useState<string>("");
  const [loadingStripeData, setLoadingStripeData] = useState(true);

  // Fetch subscription plans
  const { data: plansData, isLoading: isLoadingPlans } = useGetSubscriptionPlansQuery();

  // Lazy queries for Stripe data
  const [getStripeCustomer] = useLazyGetStripeCustomerQuery();
  const [getPaymentMethods, { data: paymentMethodsData, isLoading: isLoadingPaymentMethods, isFetching: isFetchingPaymentMethods }] =
    useLazyGetPaymentMethodsQuery();

  const { control, handleSubmit, setValue, watch } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      subscription: "",
      paymentMethod: "",
    },
  });

  const selectedPaymentMethodId = watch("paymentMethod");
  const selectedSubscriptionId = watch("subscription");

  // Load Stripe customer and payment methods on mount
  useEffect(() => {
    const loadStripeData = async () => {
      if (!operator?.id) return;

      try {
        setLoadingStripeData(true);

        // Get Stripe customer ID
        const customerResult = await getStripeCustomer({ providerId: operator.id }).unwrap();

        if (customerResult.stripeCustomerId) {
          setStripeCustomerId(customerResult.stripeCustomerId);

          // Load payment methods
          await getPaymentMethods({ customerId: customerResult.stripeCustomerId });
        }
      } catch (error) {
        console.error("Error loading Stripe data:", error);
      } finally {
        setLoadingStripeData(false);
      }
    };

    loadStripeData();
  }, [operator?.id, getStripeCustomer, getPaymentMethods]);

  const handleCardAdded = async (paymentMethodId: string) => {
    setShowAddCard(false);
    setValue("paymentMethod", paymentMethodId);

    // Reload payment methods
    if (stripeCustomerId) {
      await getPaymentMethods({ customerId: stripeCustomerId });
    }
  };

  const handleRefreshPaymentMethods = async () => {
    if (stripeCustomerId) {
      await getPaymentMethods({ customerId: stripeCustomerId });
    }
  };

  const onSubmit = handleSubmit(
    async (data) => {
      if (!operator?.id) {
        addToast("danger", "Operator not found");
        return;
      }

      if (isSubmitting) return;

      setIsSubmitting(true);

      try {
        // Call the subscription API
        await createSubscription({
          providerId: operator.id,
          subscriptionPlanId: data.subscription,
          stripePaymentMethodId: data.paymentMethod,
        }).unwrap();

        // Mark registration as complete
        await updateProfile({
          account_metadata: {
            is_register_complete: true,
            provider_code: operator.providerCode,
            type: "operator",
          },
        }).unwrap();

        addToast("success", "Welcome! Your account is ready");
        onPlanSelected();
      } catch (error) {
        console.error("Subscription selection error:", error);
        getErrorMessages(error as FetchBaseQueryError | SerializedError).forEach((m) => addToast("danger", m));
        setIsSubmitting(false);
      }
    },
    (errors) => {
      // Handle validation errors with toast
      if (errors.subscription?.message) {
        addToast("danger", errors.subscription.message);
      }
      if (errors.paymentMethod?.message) {
        addToast("danger", errors.paymentMethod.message);
      }
    }
  );

  // Show loading while fetching plans or Stripe data
  if (isLoadingPlans || loadingStripeData) {
    return (
      <Panel className="relative h-[600px] w-full max-w-[1040px] p-8">
        <Loading className="rounded-lg" />
      </Panel>
    );
  }

  // Get all subscription plans (no filtering by type)
  const subscriptionPlans = plansData?.subscriptions ?? [];
  const paymentMethods = paymentMethodsData ?? [];

  // Get selected subscription plan
  const selectedPlan = subscriptionPlans.find((plan) => plan.subscriptionId === selectedSubscriptionId);

  return (
    <Panel className="relative w-full max-w-[1040px] p-8">
      {isSubmitting && <Loading className="rounded-lg" />}

      <div className="mb-8">
        <GeneralLogo />
      </div>

      <form onSubmit={onSubmit}>
        <div className="mb-6 flex flex-col">
          <Typography variant="h3">Select Your Subscription Plan</Typography>
          <Typography variant="paragraph" className="mt-2 text-neutral-dark-gray">
            Choose the plan that best fits your business needs
          </Typography>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left Column - Subscription Plan Selection */}
          <div className={stripeCustomerId ? "lg:col-span-2" : "lg:col-span-3"}>
            <Typography variant="title" className="mb-4">
              Subscription Plans
            </Typography>
            <Controller
              name="subscription"
              control={control}
              render={({ field }) => (
                <SubscriptionGroup value={field.value} onChange={(value) => field.onChange(value)}>
                  {subscriptionPlans.map((plan) => {
                    const disabledInfo = getDisabledPlanInfo(plan.slug);
                    return (
                      <SubscriptionGroup.Option
                        key={plan.subscriptionId}
                        value={plan.subscriptionId}
                        title={plan.subscriptionName}
                        description={plan.subscriptionDescription ?? ""}
                        pricing={`${formatCurrency(plan.subscriptionFee, 0, "AUD")} /month`}
                        iconSvg={plan.iconSvg ?? undefined}
                        trialDays={plan.trialDays}
                        disabled={disabledInfo.disabled}
                        disabledReason={disabledInfo.reason}
                      />
                    );
                  })}
                </SubscriptionGroup>
              )}
            />
          </div>

          {/* Right Column - Summary Section - only show when stripe customer exists */}
          {stripeCustomerId && (
            <div className="flex flex-col gap-6">
              <div>
                <Typography variant="title" className="mb-4">
                  Summary
                </Typography>

                {/* Payment Method Selection */}
                {stripeCustomerId ? (
                  <div className="mb-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Typography variant="action" className="text-neutral-dark-gray">
                        Payment Method
                      </Typography>
                      {!showAddCard && (
                        <Button
                          variant="tertiary-light"
                          size="sm"
                          type="button"
                          onClick={handleRefreshPaymentMethods}
                          disabled={isFetchingPaymentMethods}
                        >
                          {isFetchingPaymentMethods ? "Refreshing..." : "Refresh"}
                        </Button>
                      )}
                    </div>

                    {isLoadingPaymentMethods ? (
                      <div className="border-neutral-light-gray relative min-h-[100px] rounded-lg border">
                        <Loading className="rounded-lg" />
                      </div>
                    ) : (
                      <>
                        {/* Existing Payment Methods */}
                        {paymentMethods.length > 0 && !showAddCard && (
                          <div className="relative">
                            {isFetchingPaymentMethods && <Loading className="rounded-lg" />}
                            <div className="-m-1 grid max-h-[180px] gap-2 overflow-y-auto p-1">
                              {paymentMethods.map((pm) => (
                                <PaymentMethodCard
                                  key={pm.paymentMethodId}
                                  paymentMethod={pm}
                                  isSelected={selectedPaymentMethodId === pm.paymentMethodId}
                                  onSelect={(id) => setValue("paymentMethod", id)}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Add New Card Section */}
                        {showAddCard ? (
                          <div className="bg-primary/5 flex flex-col gap-2 rounded-lg border border-primary p-4">
                            <Typography variant="action">Add New Card</Typography>
                            <AddCardForm
                              key={addCardKey}
                              providerId={operator?.id || ""}
                              countryCode={operator?.country}
                              onCardAdded={handleCardAdded}
                              onCancel={() => {
                                setShowAddCard(false);
                                setAddCardKey((prev) => prev + 1);
                              }}
                            />
                          </div>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            type="button"
                            className="w-full"
                            onClick={() => setShowAddCard(true)}
                            disabled={isFetchingPaymentMethods}
                          >
                            + Add New Card
                          </Button>
                        )}

                        {paymentMethods.length === 0 && !showAddCard && !isFetchingPaymentMethods && (
                          <Typography variant="paragraph" className="py-2 text-center text-neutral-dark-gray">
                            No payment methods found. Add a new card to continue.
                          </Typography>
                        )}
                      </>
                    )}
                  </div>
                ) : !loadingStripeData ? (
                  <div className="mb-4 rounded-lg border border-warning bg-warning/10 px-4 py-3 text-warning">
                    <Typography variant="paragraph" className="text-warning">
                      No Stripe customer configured for this provider. Please contact support.
                    </Typography>
                  </div>
                ) : null}

                {/* Pricing Breakdown - only show when plan is selected */}
                {selectedPlan && (
                  <div className="space-y-4">
                    <div className="space-y-4 rounded-lg border border-neutral-mid-gray bg-neutral-gray p-4">
                      {/* Due Today */}
                      <div>
                        <Typography variant="small" className="mb-2 uppercase text-neutral-silver-gray">
                          Due Today
                        </Typography>
                        <div className="flex justify-between">
                          <Typography variant="paragraph" className="text-neutral-dark-gray">
                            Total
                          </Typography>
                          <Typography variant="title">{formatCurrency(0, 0, "AUD")}</Typography>
                        </div>
                      </div>

                      {/* After Trial Ends */}
                      {selectedPlan.trialDays && selectedPlan.trialDays > 0 && selectedPlan.trialEndsAt && (
                        <>
                          <div className="border-t border-neutral-mid-gray pt-4">
                            <Typography variant="small" className="mb-2 uppercase text-neutral-silver-gray">
                              After Trial Ends
                            </Typography>
                            <div className="flex justify-between">
                              <Typography variant="paragraph" className="text-neutral-dark-gray">
                                One-time setup fee:
                              </Typography>
                              <Typography variant="paragraph">{formatCurrency(selectedPlan.setupFee || 0, 0, "AUD")}</Typography>
                            </div>
                          </div>

                          <div className="border-t border-neutral-mid-gray pt-4">
                            <div className="flex items-center justify-between">
                              <div className="flex flex-col gap-1">
                                <Typography variant="paragraph" className="text-neutral-dark-gray">
                                  {selectedPlan.subscriptionName} Plan:
                                </Typography>
                                <Typography variant="small" className="text-neutral-silver-gray">
                                  First prorate charge on{" "}
                                  {new Date(selectedPlan.trialEndsAt).toLocaleDateString("en-AU", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </Typography>
                              </div>
                              <Typography variant="paragraph">{formatCurrency(selectedPlan.proratedAmount || 0, 0, "AUD")}</Typography>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Start Free Trial Button */}
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="primary"
                        size="lg"
                        type="submit"
                        className="w-full"
                        disabled={isSubmitting || !selectedSubscriptionId || !selectedPaymentMethodId}
                      >
                        {selectedPlan.trialDays && selectedPlan.trialDays > 0 ? "Start Free Trial" : "Subscribe"}
                      </Button>
                      {selectedPlan.trialDays && selectedPlan.trialDays > 0 && (
                        <Typography variant="small" className="text-center text-neutral-silver-gray">
                          Cancel anytime during your trial to avoid subscription charges.
                        </Typography>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </form>
    </Panel>
  );
};
